import type { MapMode, LayerGroup } from '../../../types/mapEngine';

export interface MapModeConfig {
  id: MapMode;
  label: string;
  mapboxStyle: string;
  defaultCenter: [number, number];
  defaultZoom: number;
  layerGroups: LayerGroup[];
}

const DARK_STYLE = 'mapbox://styles/mapbox/empty-v9';

export const MAP_MODES: Record<MapMode, MapModeConfig> = {
  'fts-conops': {
    id: 'fts-conops',
    label: 'CONOPS Editor',
    mapboxStyle: DARK_STYLE,
    defaultCenter: [-79.3834, 9.0714],
    defaultZoom: 14,
    layerGroups: [
      {
        id: 'infrastructure',
        label: 'Infraestructura',
        layers: [
          { id: 'base-runways',    label: 'Pistas',           color: '#e0e0e0', iconType: 'rect' },
          { id: 'base-aprons',     label: 'Plataforma',       color: '#4a4a55', iconType: 'rect' },
          { id: 'base-taxiways',   label: 'Calles de rodaje', color: '#FFD700', iconType: 'line' },
          { id: 'base-twy-labels', label: 'Desig. TWY',       color: '#FFD700', iconType: 'line' },
          { id: 'base-stands',     label: 'Puestos',          color: '#ef5350', iconType: 'dot'  },
          { id: 'base-stand-labels', label: 'Nº puesto (AIP)', color: '#ffffff', iconType: 'line' },
          { id: 'base-agl-spots',  label: 'Spots AGL',        color: '#43A047', iconType: 'dot'  },
        ],
      },
      {
        id: 'conops',
        label: 'CONOPS',
        layers: [
          { id: 'conops-aor',             label: 'Zonas AoR',          color: '#F26430', iconType: 'polygon' },
          { id: 'conops-taxi-routes',     label: 'Rutas de rodaje',    color: '#1E88E5', iconType: 'line'    },
          { id: 'conops-pushback-profiles', label: 'Retrocesos',       color: '#AB47BC', iconType: 'line'    },
          { id: 'conops-aptr',            label: 'APTR',               color: '#1E88E5', iconType: 'line'    },
          { id: 'conops-roundabout',      label: 'Roundabout',         color: '#FFD700', iconType: 'line'    },
          { id: 'conops-handover-points', label: 'Puntos transferencia', color: '#26C6DA', iconType: 'dot'  },
        ],
      },
    ],
  },

  'fts-simulation': {
    id: 'fts-simulation',
    label: 'Simulación FTS',
    mapboxStyle: DARK_STYLE,
    defaultCenter: [-79.3834, 9.0714],
    defaultZoom: 14,
    layerGroups: [
      {
        id: 'infrastructure',
        label: 'Infraestructura',
        layers: [
          { id: 'base-runways',  label: 'Pistas',           color: '#e0e0e0', iconType: 'rect' },
          { id: 'base-aprons',   label: 'Plataforma',       color: '#4a4a55', iconType: 'rect' },
          { id: 'base-taxiways', label: 'Calles de rodaje', color: '#FFD700', iconType: 'line' },
          { id: 'base-stands',   label: 'Puestos',          color: '#ef5350', iconType: 'dot'  },
          { id: 'base-agl-spots', label: 'Spots AGL',       color: '#43A047', iconType: 'dot'  },
        ],
      },
      {
        id: 'aircraft',
        label: 'Aeronaves',
        layers: [
          { id: 'aircraft-silhouettes', label: 'Siluetas', color: '#ffffff', iconType: 'dot' },
        ],
      },
      {
        id: 'conops',
        label: 'CONOPS',
        layers: [
          { id: 'conops-aor',         label: 'Zonas AoR',       color: '#F26430', iconType: 'polygon' },
          { id: 'conops-taxi-routes', label: 'Rutas de rodaje', color: '#1E88E5', iconType: 'line'    },
          { id: 'conops-aptr',        label: 'APTR',            color: '#1E88E5', iconType: 'line'    },
        ],
      },
    ],
  },

  'analysis-infrastructure': {
    id: 'analysis-infrastructure',
    label: 'Infraestructura',
    mapboxStyle: DARK_STYLE,
    defaultCenter: [-79.3834, 9.0714],
    defaultZoom: 13,
    layerGroups: [
      {
        id: 'infrastructure',
        label: 'Infraestructura',
        layers: [
          { id: 'base-runways',  label: 'Pistas',     color: '#e0e0e0', iconType: 'rect' },
          { id: 'base-aprons',   label: 'Plataforma', color: '#4a4a55', iconType: 'rect' },
          { id: 'base-taxiways', label: 'TWY',        color: '#FFD700', iconType: 'line' },
          { id: 'base-stands',   label: 'Puestos',    color: '#ef5350', iconType: 'dot'  },
        ],
      },
    ],
  },

  'analysis-heatmap': {
    id: 'analysis-heatmap',
    label: 'Heatmaps & Avanzado',
    mapboxStyle: DARK_STYLE,
    defaultCenter: [-79.3834, 9.0714],
    defaultZoom: 13,
    layerGroups: [
      {
        id: 'infrastructure',
        label: 'Infraestructura',
        layers: [
          { id: 'base-runways',  label: 'Pistas',     color: '#e0e0e0', iconType: 'rect' },
          { id: 'base-aprons',   label: 'Plataforma', color: '#4a4a55', iconType: 'rect' },
          { id: 'base-stands',   label: 'Puestos',    color: '#ef5350', iconType: 'dot'  },
        ],
      },
      {
        id: 'heatmaps',
        label: 'Heatmaps',
        layers: [
          { id: 'heatmap-congestion',    label: 'Congestión',          color: '#ef5350', iconType: 'line' },
          { id: 'heatmap-arrivals',      label: 'Llegadas',            color: '#FF7043', iconType: 'line' },
          { id: 'heatmap-departures',    label: 'Salidas',             color: '#66BB6A', iconType: 'line' },
          { id: 'heatmap-taxi-risk',     label: 'Riesgo rodaje',       color: '#FFA726', iconType: 'line' },
          { id: 'heatmap-taxi-speed',    label: 'Velocidad rodaje',    color: '#26C6DA', iconType: 'line' },
          { id: 'heatmap-pushback-risk', label: 'Riesgo pushback',     color: '#AB47BC', iconType: 'polygon' },
          { id: 'heatmap-ci-graph',      label: 'Índice centralidad',  color: '#EC407A', iconType: 'dot'  },
          { id: 'heatmap-tcf-graph',     label: 'TCF (conflicto)',     color: '#FF7043', iconType: 'line' },
          { id: 'heatmap-mht-graph',     label: 'MHT (retención)',     color: '#7E57C2', iconType: 'line' },
        ],
      },
      {
        id: 'aviplan',
        label: 'AviPLAN',
        layers: [
          { id: 'aviplan-swept-paths',      label: 'Trayectorias nariz/tren', color: '#29B6F6', iconType: 'line'    },
          { id: 'aviplan-safety-envelopes', label: 'Envolventes seguridad',   color: '#66BB6A', iconType: 'polygon' },
          { id: 'aviplan-jet-blast',        label: 'Contornos jet blast',     color: '#FF7043', iconType: 'polygon' },
        ],
      },
    ],
  },

  'analysis-pushback': {
    id: 'analysis-pushback',
    label: 'Análisis Pushback',
    mapboxStyle: DARK_STYLE,
    defaultCenter: [-79.3834, 9.0714],
    defaultZoom: 14,
    layerGroups: [
      {
        id: 'infrastructure',
        label: 'Infraestructura',
        layers: [
          { id: 'base-runways',  label: 'Pistas',     color: '#e0e0e0', iconType: 'rect' },
          { id: 'base-aprons',   label: 'Plataforma', color: '#4a4a55', iconType: 'rect' },
          { id: 'base-taxiways', label: 'TWY',        color: '#FFD700', iconType: 'line' },
          { id: 'base-stands',   label: 'Puestos',    color: '#ef5350', iconType: 'dot'  },
        ],
      },
      {
        id: 'aviplan',
        label: 'AviPLAN',
        layers: [
          { id: 'aviplan-swept-paths',      label: 'Trayectorias nariz/tren', color: '#29B6F6', iconType: 'line'    },
          { id: 'aviplan-safety-envelopes', label: 'Envolventes seguridad',   color: '#66BB6A', iconType: 'polygon' },
          { id: 'aviplan-jet-blast',        label: 'Contornos jet blast',     color: '#FF7043', iconType: 'polygon' },
        ],
      },
    ],
  },
};
